import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Hook for synchronizing tab state with URL search params
 * Enables shareable URLs and proper browser back/forward navigation
 *
 * @param options.resetQueryOnTabChange When true, changing tabs replaces the query string with only `tab=<value>` (drops filters; used on Tasks page).
 */
export const useTabQuerySync = (defaultTab = 'overview', options?: { resetQueryOnTabChange?: boolean }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    /** Must match URL on first paint so Radix Tabs and `fillAvailable` tables measure the visible panel (refresh with `?tab=`). */
    const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') ?? defaultTab);
    const resetQueryOnTabChange = options?.resetQueryOnTabChange ?? false;

    // Read initial tab from URL on mount and ensure tab param is always in URL
    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        if (tabFromUrl) {
            setActiveTab(tabFromUrl);
        } else {
            // Set default tab in URL so each tab has a shareable URL
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('tab', defaultTab);
            setSearchParams(newSearchParams, { replace: true });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run on mount only

    // Update URL when tab changes
    const handleTabChange = useCallback(
        (newTab: string) => {
            setActiveTab(newTab);

            if (resetQueryOnTabChange) {
                setSearchParams(new URLSearchParams({ tab: newTab }), { replace: true });
                return;
            }

            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('tab', newTab);
            setSearchParams(newSearchParams, { replace: true });
        },
        [resetQueryOnTabChange, searchParams, setSearchParams],
    );

    // Listen for browser back/forward navigation
    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        if (tabFromUrl && tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [searchParams, activeTab]);

    return {
        activeTab,
        setActiveTab: handleTabChange,
    };
};
